/**
 * Challenges list – active video challenges. Realtime subscribe.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeActiveChallenges } from '../../domain/usecases/challenges';
import { challengeSubmissionRepo } from '../../data/repositories/ChallengeSubmissionRepository';
import { CHALLENGE_CATEGORY_LABELS } from '../../domain/entities/Challenge';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Card from '../../shared/ui/Card.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import Icon from '../../shared/ui/Icon.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './ChallengesPage.module.css';

const CATEGORY_ICONS = { powerLifter: 'barbell', strongman: 'flame', bodyBuilder: 'user' };

function formatTimeLeft(endDate) {
  const end = new Date(endDate);
  const now = new Date();
  if (end <= now) return 'Ended';
  const d = Math.ceil((end - now) / 86400000);
  if (d === 1) return '1 day left';
  if (d < 7) return `${d} days left`;
  const w = Math.ceil(d / 7);
  return w === 1 ? '1 week left' : `${w} weeks left`;
}

export function ChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [submissionCounts, setSubmissionCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeActiveChallenges(30, (list) => {
      setChallenges(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (challenges.length === 0) return;
    Promise.all(
      challenges.map(async (c) => {
        const list = await challengeSubmissionRepo.listByChallenge(c.id, 500);
        return [c.id, list.length];
      })
    ).then((pairs) => {
      const map = {};
      pairs.forEach(([id, count]) => { map[id] = count; });
      setSubmissionCounts(map);
    });
  }, [challenges]);

  return (
    <div className={styles.page}>
      <Display as="h1" size="l">Challenges</Display>
      <Body tone="secondary" style={{ marginBottom: '1.25rem' }}>
        Tap a challenge to submit your video and vote for others.
      </Body>

      {loading ? (
        <div className={styles.skeletonList}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} width="100%" height={120} radius="var(--radius-md)" style={{ marginBottom: '0.75rem' }} />
          ))}
        </div>
      ) : challenges.length === 0 ? (
        <EmptyState icon="flame" title="No challenges" body="New challenges coming soon." />
      ) : (
        <ul className={styles.list}>
          {challenges.map((c) => {
            const isActive = c.status === 'active' && (!c.endDate || new Date(c.endDate) > new Date());
            return (
              <li key={c.id}>
                <Card className={styles.card}>
                  <div className={styles.cardHeader}>
                    <span className={styles.cardIcon}>
                      <Icon name={CATEGORY_ICONS[c.category] || 'trophy'} size="md" />
                    </span>
                    <Badge variant={isActive ? 'red' : 'neutral'}>
                      {isActive ? 'Live' : 'Upcoming'}
                    </Badge>
                  </div>
                  <Title as="h3" style={{ marginBottom: '0.25rem' }}>
                    {c.title || c.exercise || 'Challenge'}
                  </Title>
                  <Body tone="secondary" style={{ marginBottom: '0.5rem' }}>
                    {CHALLENGE_CATEGORY_LABELS[c.category] || ''}{' '}
                    · {submissionCounts[c.id] ?? 0} submissions
                  </Body>
                  <div className={styles.cardFooter}>
                    <Body tone="secondary" style={{ fontSize: 13 }}>
                      {formatTimeLeft(c.endDate || c.expiresAt)}
                    </Body>
                    <Button variant="secondary" size="sm" as={Link} to={`/challenges/${c.id}`}>
                      View
                    </Button>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
